import { LinearProgress } from "@mui/material"
import { useRouter } from "next/router"
import { FC, useEffect, useMemo, useRef, useState } from "react"

import { DropdownRef } from "src/components/common/dropdown"

import { create } from "src/helpers/bem"
import { checkNullOrEmpty } from "src/helpers/string"
import { getFilesById } from "src/queriesXRM/file"
import { getFormById } from "src/queriesXRM/form"
import { getPermission } from "src/queriesXRM/permission"
import { getPlacements } from "src/queriesXRM/placement"
import { getSalesorderById } from "src/queriesXRM/salesorder"

import { mergeAccount, useAccount } from "src/states/account"
import {
  getBookingDetail,
  setBooking,
  setBookingDetails,
  setPlacements,
  useBooking,
} from "src/states/bookingDetail"
import { useLocale } from "src/states/locale"

import { SharedPageProps } from "src/types/SharedPageProps"

import { xRMApiField, xRMApiFile, xRMApiForm } from "src/types/xRM"

import styles from "./BookingDetail.module.scss"

import { BookingDetailCoExhibitorTable } from "./BookingDetailCoExhibitorTable"
import { BookingDetailFileManagement } from "./BookingDetailFileManagement"
import { BookingDetailForm } from "./BookingDetailForm"
import { BookingDetailInfo } from "./BookingDetailInfo"
import { BookingDetailOverview } from "./BookingDetailOverview"
import { BookingDetailPlacement } from "./BookingDetailPlacement"

const bem = create(styles, "BookingDetail")

export const loadBooking = async () => {
  const { booking: bookingDetail } = await getBookingDetail()
  if (!bookingDetail?.id) {
    return
  }
  const bookingResult = await getSalesorderById(bookingDetail?.id)
  const formResult = await getFormById(
    bookingResult?.id ?? "",
    "Main",
    bookingResult?.phase,
  )
  let formatedForm = []
  if (bookingResult?.phase !== "Draft") {
    formatedForm = formResult?.map((form: xRMApiForm) => ({
      ...form,
      fields: form?.fields?.map(async (field) => {
        if (field.type === "file") {
          const defaultFiles = await getFilesById(
            "Attachment",
            bookingDetail?.id,
          )
          const filteredDefaultFiles = defaultFiles?.filter(
            (f: xRMApiFile) => f.formControl === "",
          ) // Before filtered name of field
          return {
            ...field,
            files: filteredDefaultFiles,
          } as xRMApiField
        }
        return {
          ...field,
        }
      }),
    }))
  }
  let data = {
    activeStep: 0,
    steps: formatedForm,
  }
  if (bookingResult?.phase === "Draft" && formResult?.currentForm) {
    data = JSON.parse(formResult?.currentForm)
  }
  setBooking(bookingResult)
  setBookingDetails({
    ...data,
  })
}

export const loadPlacements = async () => {
  const { booking: bookingDetail } = await getBookingDetail()

  const placements = await getPlacements(bookingDetail?.id)
  setPlacements(placements)
}

export const BookingDetail: FC<SharedPageProps> = (props) => {
  const { pages, messages } = props
  const dropdownRef = useRef<DropdownRef>(null)
  const [loading, setLoading] = useState(false)
  const locale = useLocale()
  const { permissions } = useAccount()
  const { loadingSalesorderDeletion, booking, detail } = useBooking()
  // TESTING
  // const loadingSalesorderDeletion = false
  // const booking = undefined
  const router = useRouter()

  const phase = booking?.phase
  const isActiveBooking =
    booking?.exhibition?.id === process.env.NEXT_PUBLIC_XRM_EVENT_ID

  const loadPermissions = async () => {
    const permission = await getPermission()
    mergeAccount({
      permissions: permission && permission?.length > 0 ? permission : [],
    })
  }

  const loadData = async () => {
    await loadBooking()
    await loadPlacements()
    setLoading(false)
  }

  useEffect(() => {
    loadPermissions()
  }, [])

  useEffect(() => {
    if (booking) {
      setLoading(true)
      loadData()
    }
  }, [booking?.id, locale])

  const redirectBookingForm = useMemo(
    () =>
      pages?.data?.nodes?.filter(
        (page) =>
          page?.language?.slug === locale &&
          page?.template?.templateName === "Booking Form Template",
      )?.[0],
    [pages, locale],
  )

  const hideCoexhibitor = booking?.exhibition?.hideCoexhibitor

  const hasAdminPermission = permissions?.some(
    (el) => el.salesorderId === booking?.id && el.role === "Administrator",
  )

  if (!booking) {
    return (
      <div className={bem()}>
        <div className={bem("no-data")}>
          <h3>{messages.pages.booking.overview.noData}</h3>
        </div>
      </div>
    )
  }

  if (loading || loadingSalesorderDeletion) {
    return (
      <div className={bem()}>
        <div className={bem("loading")}>
          <LinearProgress color="primary" />
        </div>
      </div>
    )
  }

  return (
    <div className={bem()}>
      <div className={bem("header")}>
        <div className={bem("header__wrapper")}>
          <h3 className={bem("header__headline")}>
            {messages.pages.booking.detail.header.title.title}
          </h3>
          <h5
            className={bem("header__headline")}
          >{`${messages.pages.booking.detail.header.title.orderPrefix} ${booking?.orderNumber}`}</h5>
          {/* NOT IN MOCKUP */}
          {/* {phase && (
            <span className={bem("header__status", phase)}>
              {(messages.pages.booking.overview.table.phase.items as any)?.[
                phase
              ] ?? phase}
            </span>
          )} */}
        </div>
        <div className={bem("header__chips")}>
          <div className={bem("header__chips__chip", "hall")}>
            <h3>{checkNullOrEmpty(booking?.standhall) ?? "-"}</h3>
            <p>{messages.pages.booking.detail.header.hall}</p>
          </div>
          <div className={bem("header__chips__chip", "stand")}>
            <h3>{checkNullOrEmpty(booking?.standnumber) ?? "-"}</h3>
            <p>{messages.pages.booking.detail.header.stand}</p>
          </div>
          <div className={bem("header__chips__chip", "area")}>
            <h3>{`${
              booking?.assignedStandSize ?? booking?.requestedAreaMin ?? "-"
            } ${messages.pages.booking.detail.header.area.unit}`}</h3>
            <p>{messages.pages.booking.detail.header.area.title}</p>
          </div>
        </div>
        {/* NOT IN MOCKUP */}
        {/* <BookingDetailDropdown
          ref={dropdownRef}
          documentHeadline={page?.data?.title}
          header={header}
          isActiveBooking={isActiveBooking}
          messages={messages}
        /> */}
      </div>
      <BookingDetailOverview bookingId={booking?.id!} {...props} />
      {/* NOT IN MOCKUP */}
      {/* <BookingDetailBanner
        handleButtonClick2={() => {}}
        messages={messages}
        showContent2={phase !== "Draft"}
        showContent1={
          adminPermission &&
          phase !== "Draft" &&
          !hideCoexhibitor &&
          isActiveBooking
        }
      /> */}
      {phase !== "Draft" && (
        <>
          <BookingDetailPlacement messages={messages} />
          <BookingDetailFileManagement messages={messages} />
        </>
      )}
      <div className={bem("info")}>
        {phase === "Draft" ? (
          <BookingDetailForm messages={messages} />
        ) : (
          hasAdminPermission && <BookingDetailInfo messages={messages} />
        )}
        {phase !== "Draft" && !hideCoexhibitor && hasAdminPermission && (
          <BookingDetailCoExhibitorTable
            isActiveBooking={isActiveBooking}
            messages={messages}
          />
        )}
      </div>
    </div>
  )
}
